import React, { useState } from 'react';
import { faKey, faGear, faSave, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@quyva/shared-ui';
import { useSettingsContext } from '../../../context';
import { SettingSections } from '../settingsView';

const ProviderKeyInput = ({ groupId, provider, keysByProvider, setKeysByProvider }) => {
    const [visible, setVisible] = useState(false);
    const compoundKey = `${groupId}:${provider.id}`;
    const value = keysByProvider[compoundKey] || '';

    const handleChange = (e) =>
        setKeysByProvider((prev) => ({ ...prev, [compoundKey]: e.target.value }));

    const inputClass =
        'w-full border border-backgrounds-primary100 rounded px-3 py-2 text-sm text-texts-secondary focus:outline-none focus:ring-1 focus:ring-backgrounds-primary300';

    return (
        <div
            key={provider.id}
            className="rounded-lg border border-backgrounds-primary100 p-5 bg-backgrounds-white"
        >
            <label className="flex items-center gap-2 font-semibold text-sm text-texts-secondary300">
                <FontAwesomeIcon icon={faKey} />
                {provider.label}
            </label>

            {provider.multiline ? (
                <div className="mt-3">
                    <textarea
                        rows={6}
                        placeholder={provider.placeholder}
                        value={value}
                        onChange={handleChange}
                        className={`${inputClass} resize-y font-mono`}
                        spellCheck={false}
                    />
                    {provider.hint && (
                        <p className="mt-1 text-xs text-texts-secondary300">{provider.hint}</p>
                    )}
                </div>
            ) : (
                <div className="mt-3 flex items-center gap-2">
                    <input
                        type={visible ? 'text' : 'password'}
                        placeholder={provider.placeholder}
                        value={value}
                        onChange={handleChange}
                        className={inputClass}
                    />
                    <button
                        type="button"
                        onClick={() => setVisible((v) => !v)}
                        className="text-texts-secondary300 hover:text-texts-secondary flex-shrink-0 px-1"
                        title={visible ? 'Hide key' : 'Show key'}
                    >
                        <FontAwesomeIcon icon={visible ? faEyeSlash : faEye} />
                    </button>
                </div>
            )}
        </div>
    );
};

const KeysGroupView = ({ group, keysByProvider, setKeysByProvider }) => {
    return (
        <div className="mb-10">
            <div className="text-lg font-semibold text-texts-secondary300 mb-6">
                {group.title}
            </div>

            <div className="space-y-5">
                {group.providers.map((provider) => (
                    <ProviderKeyInput
                        key={provider.id}
                        groupId={group.id}
                        provider={provider}
                        keysByProvider={keysByProvider}
                        setKeysByProvider={setKeysByProvider}
                    />
                ))}
            </div>
        </div>
    );
};

const GROUPS = [
    {
        id: 'llm',
        title: 'LLM Providers',
        providers: [
            { id: 'openai', label: 'OpenAI', placeholder: 'OpenAI API key' },
            {
                id: 'gemini',
                label: 'Gemini',
                placeholder: 'Plain API key  —or—  paste the full Vertex AI JSON blob here',
                multiline: true,
                hint: 'Accepts a plain Gemini API key or the full Vertex AI JSON credentials blob.',
            },
            { id: 'together', label: 'Together', placeholder: 'Together API key' },
        ],
    },
    {
        id: 'crawler',
        title: 'Web Crawling',
        providers: [
            { id: 'firecrawl', label: 'Firecrawl', placeholder: 'Firecrawl API key' },
            { id: 'spidercrawl', label: 'Spider Crawl', placeholder: 'Spider Crawl API key' },
        ],
    },
];

const SystemSettings = () => {
    const {
        systemKeysByProvider,
        setSystemKeysByProvider,
        runWithSaving,
        saveSystemSection,
        isSystemSettingsDirty,
        isDbOnly,
    } = useSettingsContext();

    const displayGroups = isDbOnly
        ? GROUPS.filter((g) => g.id === 'llm')
        : GROUPS;

    return (
        <div className="flex flex-col w-full justify-center text-texts-secondary">

            <div className="flex items-center justify-between font-semibold mb-2 py-3 px-4 text-texts-secondary300 bg-backgrounds-primary100 truncate">
                <div className="flex items-center">
                    <FontAwesomeIcon icon={faGear} className="mr-2" />
                    <div className="uppercase truncate">
                        {SettingSections.SYSTEM_SETTINGS}
                    </div>
                </div>

                <Button
                    type="success"
                    icon={faSave}
                    text="Save"
                    disabled={!isSystemSettingsDirty}
                    hoverText="Save system settings"
                    onClick={() =>
                        runWithSaving(async () => {
                            await saveSystemSection();
                        })
                    }
                />
            </div>

            <div className="h-[calc(100vh-210px)] w-full overflow-auto px-6 py-6">
                {displayGroups.map((group) => (
                    <KeysGroupView
                        key={group.id}
                        group={group}
                        keysByProvider={systemKeysByProvider}
                        setKeysByProvider={setSystemKeysByProvider}
                    />
                ))}
            </div>
        </div>
    );
};

export default SystemSettings;
